import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import { dnbComLog, isNotUndefinedOrNull, LogSeverity } from "@/shared/FunctionCore";
import { getCompanyInformationData } from "@/services/businessDirectory";
import {
  companyInformationResponseData,
} from "@/app/pages/companyInformation/ConfigAndInterface";

import Breadcrumb from "@/components/breadCrumb";
import Loader from "@/components/loader";
import DandBHoovers from "@/components/banners/DandBhoovers";
import HeadingBlue from "@/components/banners/HeadingBlue";
import Companies from "@/app/pages/companyInformation/companies";
import RegionsAndIndustries from "@/app/pages/companyInformation/regionsAndIndustries";
import "@/app/pages/companyInformation/companyInformation.scss";
import {setDigitalData} from "@/services/setDigitalData";
import Overlay from "@/components/overlay";
import { usePageURL } from "@/shared/customHooks";
import { industryNotFoundErrorMessage } from "@/shared/constant";
import MetaDataTags from "@/components/metaDataTags/MetaDataTags";

const CompanyInformation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { countryCode } = usePageURL();
  const [companyInformationData, setCompanyInformationData] =
    useState<companyInformationResponseData>();
  const [isLoading, setIsLoading] = useState(false);
  const [regionRedirection, setRegionRedirection] = useState("");
  const [headingBannerData, setHeadingBannerData] = useState({});
  const [searchParams] = useSearchParams();
  const [tableData, setTableData] = useState<any>(null);
  const [regionsAndIndustriesData, setRegionsAndIndustriesData] =
    useState<any>(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [regionName, setRegionName] = useState('');
  const [countryName, setCountryName] = useState('');
  const [cityName, setCityName] = useState('');
  const [countryAndCitiesString, setCountryAndCitiesString] = useState([])
  const [browserTitle, setBrowserTitle] = useState('Dun & Bradstreet'); 
  const [metaDescription, setMetaDescription] = useState(''); 

  setDigitalData({gcpPageName: 'informationPage'});
  const navigate = useNavigate();

  const getFormattedName = (regionName) => {
    if(isNotUndefinedOrNull(regionName)) {
      let updatedRegionName = regionName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
      return `${updatedRegionName}` ;
    } else {
      return '';
    }
  }

  // Set Hoovers Data
  const hooverData = {};
  hooverData["heading"] = t("industryAnalysis.dAndBHoovers.heading");
  hooverData["content"] = t("industryAnalysis.dAndBHoovers.content");
  hooverData["buttonLabel"] = t("industryAnalysis.dAndBHoovers.buttonLabel");
  hooverData["buttonRedirectionLink"] = t("industryAnalysis.dAndBHoovers.buttonRedirectionLink");

 // Set Heading Banner Data
 const updateHeadingBannerData = (industryPropertiesTitle, cityName, regionName, countryName )  => {
 const heading = {};
 heading["titleSuffix"] = "";
 heading["isTintClassName"] = false;
 heading["title"] = `${industryPropertiesTitle} ${t(
   "companyInformation.BannerHeader.headingSuffix"
 )} ${cityName ? `${getFormattedName(cityName)},`:''} ${regionName ? `${getFormattedName(regionName)},`: ''} ${
  countryName
 }`
 setHeadingBannerData(heading);
}

  // To set Table section data and Region and Industries section data
  const setCompanyInformationSectionData = (responseData) => {
    const {
      naicsCodes,
      industryPropertiesTitle,
      companyInformationCompany,
      industryDescription,
      candidatesMatchedQuantity,
      pageSize,
      currentPageNumber,
      candidatesMatchedQuantityInt,
      firstHit,
      lastHit,
      candidatesMatchedQuantityIsNullOrZeroString,
      countryMapValue,
      cityStateProvinceCountry
    } = responseData;
    const tableData = {
      naicsCodes,
      industryPropertiesTitle,
      companyInformationCompany,
      industryDescription,
      candidatesMatchedQuantity,
      pageSize,
      currentPageNumber,
      candidatesMatchedQuantityInt,
      firstHit,
      lastHit,
      candidatesMatchedQuantityIsNullOrZeroString,
      countryMapValue
    };
    if (cityStateProvinceCountry) setCountryAndCitiesString(cityStateProvinceCountry.replace('&nbsp;','').split(','));
    tableData["page"] = Number(searchParams.get("page"));
    setTableData(tableData); // Set Table Data

    const {
      companyInformationGeos,
      relatedIndustries,
    } = responseData;
    const regionsAndIndustriesData = {
      industryPropertiesTitle,
      companyInformationGeos,
      relatedIndustries,
    };
    if(isNotUndefinedOrNull(regionRedirection)){
      let locationPath = decodeURIComponent(location.pathname);
      let splittedPath = locationPath.split("/");      
      regionsAndIndustriesData["redirection"] = splittedPath[2];
    } else {
      regionsAndIndustriesData["redirection"] = regionRedirection;
    }
    setRegionsAndIndustriesData(regionsAndIndustriesData); // Set Regions and Industries Data
  };

  useEffect(() => {
    if ((Object.keys(headingBannerData).length || headingBannerData?.title) && (countryAndCitiesString && countryAndCitiesString.length)) {
      const prefix = countryAndCitiesString.length % 2 === 1 ? 'Find' : 'Discover';
      setBrowserTitle(`${prefix} ${headingBannerData.title.replace(/\s+/g, ' ')} - Dun & Bradstreet`);
      setMetaDescription(`Detailed info on ${headingBannerData.title.replace(/\s+/g, ' ')}, including financial statements, sales and marketing contacts, top competitors, and firmographic insights.`);
    }
  }, [headingBannerData, countryAndCitiesString])

  const fetchSubstringForRedirection = (regionName) => {
    if(regionName) {
      let index = location.pathname.indexOf(`.${regionName}.`);
      let b = location.pathname.slice(0, index + 1 + (regionName.length));
      return `${b}.html`;
    } 
    else {
      return '';
    }
  }

  const setBreadcrumbsData = (breadCrumb, regionOrCountry, countryIsoCode, regionName, cityName) => {
    let initialBreadcrumb = [
      { name: t("homeLabel"), url: t("homeRedirection"), useAnchorTag: true },
      { name: t("business_directory"), url: t("bdLandingPageRedirectionLink") }
    ];
    breadCrumb.map(bread => {
      let breadscrumbObj = {name: bread.name, url: `/business-directory/industry-analysis.${bread.url}.html`}
      initialBreadcrumb.push(breadscrumbObj)
    });
    if(regionOrCountry && regionName) {
      initialBreadcrumb.push({name: getFormattedName(regionOrCountry), url: fetchSubstringForRedirection(countryIsoCode)});
    } else {
      initialBreadcrumb.push({name: getFormattedName(regionOrCountry), url: ''});
    }
    if(regionName && cityName) {
      initialBreadcrumb.push({name: getFormattedName(regionName), url: fetchSubstringForRedirection(regionName)});
    } else if(regionName) {
      initialBreadcrumb.push({name: getFormattedName(regionName), url: ''});
    }
    if(cityName) {
      initialBreadcrumb.push({name: getFormattedName(cityName), url: ''});
    }
    setBreadcrumbs(initialBreadcrumb);
  }

  const fetchData = () => {
    setIsLoading(true);
    try {
      let locationPath = decodeURIComponent(location.pathname);
      let splittedPath = locationPath.split("/");
      const urlData = splittedPath[2];
      setRegionRedirection(urlData);
      const requestBody = {
        pageNumber: Number(searchParams.get("page")),
      };
      let regionName = '';
      let cityName = '';
      if (isNotUndefinedOrNull(urlData)) {
        requestBody["industryPath"] = urlData.split(".")[1];
        requestBody["countryIsoTwoCode"] = urlData.split(".")[2];
        if (
          isNotUndefinedOrNull(urlData.split(".")[3]) &&
          urlData.split(".")[3] !== "html"
        ) {
          requestBody["regionName"] = urlData.split(".")[3];
          setRegionName(urlData.split(".")[3]);
          regionName = urlData.split(".")[3];
        }
        if (
          isNotUndefinedOrNull(urlData.split(".")[4]) &&
          urlData.split(".")[4] !== "html"
        ) {
          requestBody["cityName"] = urlData.split(".")[4];
          setCityName(urlData.split(".")[4]);
          cityName = urlData.split(".")[4];
        }
      }
      const bdLandingPageRedirectionLink = t("bdLandingPageRedirectionLink");

      getCompanyInformationData(requestBody)
        .then((res) => {
          if (res.status === 404) {
            navigate(bdLandingPageRedirectionLink);
          }
          if (res && res.data !== null) {
            if (res.data && Object.keys(res.data).length === 0) {
              navigate(bdLandingPageRedirectionLink);
            } else {
              setCompanyInformationSectionData(res.data);
              setCompanyInformationData(res.data);
              setCountryName(res.data["countryMapValue"]);
              setBreadcrumbsData(res.data.breadcrumb, res.data["countryMapValue"], requestBody["countryIsoTwoCode"], regionName, cityName);
              updateHeadingBannerData(res.data.industryPropertiesTitle, cityName, regionName, res.data["countryMapValue"])
              setIsLoading(false);
            }
          }

        })
        .catch((err) => {
          if (err.status === 404) {
            navigate(bdLandingPageRedirectionLink);
          }
          if (err.status === 500) {
            navigate(`/business-directory.html?errorMessage=${industryNotFoundErrorMessage}#BusinessDirectoryPageNumber=1&ContactDirectoryPageNumber=1&MarketplacePageNumber=1&SiteContentPageNumber=1&tab=Business%20Directory`)
          }
          setIsLoading(false);
          dnbComLog(LogSeverity.ERROR, err.message);
        });
    } catch {
      setIsLoading(false);
      dnbComLog(LogSeverity.ERROR, "Something went wrong");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageValue = params.get("page");
    if (location.pathname || pageValue) {
      fetchData();
    }
  }, [location.pathname, location.search]);

  return (
    <>
      <MetaDataTags
        title={browserTitle}
        description={metaDescription}
      />
      <ToastContainer position="top-center" />
      {isLoading ? (
        <Loader inProgress={isLoading} />
      ) : companyInformationData ? (
        <>
          {headingBannerData && <HeadingBlue props={headingBannerData} />}
          <div className="breadcrumbContainer">
            <Breadcrumb
              breadcrumbs={breadcrumbs}
              forComponent="businessDirectory"
              delimiter=">"
              styling="font-style-light"
            />
          </div>
          {headingBannerData && <Companies props={tableData} descriptionHeading={headingBannerData}/>}
          <RegionsAndIndustries props={regionsAndIndustriesData} />
          <DandBHoovers props={hooverData} />
        </>
      ) : (
        <></>
      )}
      <Overlay
        countryCode={countryCode}
        isEmailDisplay={['pl', 'us'].includes(countryCode)}
        isPhoneDisplay={['de', 'at', 'ch','hu','us'].includes(countryCode)}
      />
    </>
  );
};

export default CompanyInformation;
